import {
  MagnetIcon,
  ShoppingCartIcon,
  PresentationIcon,
  CalendarIcon,
  TrophyIcon,
  UsersIcon,
  PackageIcon,
  VideoIcon,
  PhoneCallIcon,
  FlameIcon,
} from 'lucide-react'

export type Objective = {
  id: string
  label: string
  description: string
  icon: any
  funnelTypeIds: string[]
}

export const OBJECTIVES: Objective[] = [
  {
    id: 'grow-audience',
    label: 'Grow my audience',
    description: 'Collect emails and build a list of interested people.',
    icon: UsersIcon,
    funnelTypeIds: ['lead'],
  },
  {
    id: 'sell-product',
    label: 'Sell a product',
    description: 'Sell digital or physical products with a smooth checkout.',
    icon: PackageIcon,
    funnelTypeIds: ['product'],
  },
  {
    id: 'host-webinar',
    label: 'Host a webinar',
    description: 'Present online and pitch your offer at the end.',
    icon: VideoIcon,
    funnelTypeIds: ['presentation'],
  },
  {
    id: 'book-calls',
    label: 'Book appointments',
    description: 'Get qualified people to schedule a call with you.',
    icon: PhoneCallIcon,
    funnelTypeIds: ['appointment'],
  },
  {
    id: 'build-community',
    label: 'Build a community',
    description: 'Run a challenge or event to rally people around a goal.',
    icon: FlameIcon,
    funnelTypeIds: ['challenge'],
  },
]

export type FunnelType = {
  id: string
  name: string
  icon: any
  description: string
  bestFor: string
  strategyOverview: string
  keyPages: string[]
  bestPractices: string[]
  expectedResults: { metric: string; value: string }
  commonMistakes: string[]
}

export const FUNNEL_TYPES: FunnelType[] = [
  {
    id: 'lead',
    name: 'Lead Funnels',
    icon: MagnetIcon,
    description:
      'Get people to sign up with their email so you can stay in touch.',
    bestFor: 'Coaches, consultants, course creators starting out',
    strategyOverview:
      'Give away something helpful for free (like a guide or checklist) in exchange for an email address. This lets you build a list of people who are interested in what you do.',
    keyPages: ['Sign-up Page', 'Thank You Page', 'Welcome Emails'],
    bestPractices: [
      'Make sure your free gift solves one specific problem.',
      'Keep the sign-up form simple—just ask for their email.',
      'Give them the free gift right away on the Thank You page.',
    ],
    expectedResults: { metric: 'People who sign up', value: 'About 1 in 4' },
    commonMistakes: [
      'Asking for too much personal info upfront.',
      'Giving away something boring that nobody wants.',
    ],
  },
  {
    id: 'product',
    name: 'Product Funnels',
    icon: ShoppingCartIcon,
    description: 'Set up a page to sell your product with a simple checkout.',
    bestFor: 'E-commerce, digital product sellers',
    strategyOverview:
      'Make it super easy for people to buy your stuff. Guide them through a simple checkout and offer them something extra right before they pay.',
    keyPages: [
      'Sales Page',
      'Checkout Page',
      'Special Offer Page',
      'Order Confirmation',
    ],
    bestPractices: [
      'Add a little checkbox on the checkout page for a quick extra item.',
      'Make your special offer something that goes perfectly with what they just bought.',
      'Show reviews from happy customers near the buy button.',
    ],
    expectedResults: {
      metric: 'People who buy the extra item',
      value: 'About 1 in 5',
    },
    commonMistakes: [
      'Making them click through too many pages to pay.',
      'Offering something confusing that makes them rethink buying.',
    ],
  },
  {
    id: 'presentation',
    name: 'Presentation Funnels',
    icon: PresentationIcon,
    description: 'Present to a group online and offer something at the end.',
    bestFor: 'High-ticket offers, B2B services',
    strategyOverview:
      'Teach people something valuable on a video call to build trust. Once they see you know your stuff, they are much more likely to buy your more expensive services.',
    keyPages: [
      'Sign-up Page',
      'Welcome Page',
      'Live Video Room',
      'Replay Page',
    ],
    bestPractices: [
      'Spend the first 15 minutes teaching them a new way to look at their problem.',
      'Chat with people during the video to keep it fun.',
      'Give a special bonus to people who buy before the video replay goes away.',
    ],
    expectedResults: { metric: 'People who show up', value: 'About 1 in 3' },
    commonMistakes: [
      'Teaching too many boring details instead of the big picture.',
      'Using slides with way too much text on them.',
    ],
  },
  {
    id: 'appointment',
    name: 'Appointment Funnels',
    icon: CalendarIcon,
    description: 'Let people fill out a form and pick a time to talk to you.',
    bestFor: 'Service providers, agencies, consultants',
    strategyOverview:
      "Make sure you only talk to people who are a good fit. Have them answer a few questions and pick a time on your calendar so you don't waste time on bad calls.",
    keyPages: [
      'Intro Video Page',
      'Questionnaire',
      'Calendar Page',
      'Get Ready Page',
    ],
    bestPractices: [
      'Use a short video to explain exactly who you can help.',
      'Ask a few questions to make sure they can afford your help.',
      "Send them a text reminder before the call so they don't forget.",
    ],
    expectedResults: {
      metric: 'People who book a call',
      value: 'Most of them',
    },
    commonMistakes: [
      'Letting anyone book a call without answering questions first.',
      'Not telling them what to expect on the call.',
    ],
  },
  {
    id: 'challenge',
    name: 'Challenge Funnels',
    icon: TrophyIcon,
    description:
      'Get people excited with a multi-day event, then offer your product.',
    bestFor: 'Course creators, fitness coaches, community builders',
    strategyOverview:
      'Bring a group of people together to achieve a small goal over a few days. Once they get a quick win with your help, they will want to buy your main thing to keep going.',
    keyPages: [
      'Sign-up Page',
      'VIP Upgrade Page',
      'Daily Video Pages',
      'Final Offer Page',
    ],
    bestPractices: [
      'Offer a special "VIP Access" upgrade right after they sign up.',
      'Keep the daily homework super short (under 15 minutes).',
      'Have everyone chat in a Facebook group so it feels like a party.',
    ],
    expectedResults: { metric: 'People who buy VIP', value: 'About 1 in 10' },
    commonMistakes: [
      'Giving them way too much work to do each day.',
      'Waiting until the very last day to mention you have something for sale.',
    ],
  },
]

export type Template = {
  id: string
  title: string
  description: string
  typeId: string
  category: string
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced'
  bestForTag: string
  stepCount: number
  strategyNote: string
  gradientClass: string
}

export const TEMPLATES: Template[] = [
  {
    id: 't1',
    title: 'Simple Email Collector',
    description:
      'A super easy 2-page setup to get emails by giving away a free guide.',
    typeId: 'lead',
    category: 'Teaching & Courses',
    difficulty: 'Beginner',
    bestForTag: 'Great for beginners',
    stepCount: 2,
    strategyNote:
      'Make sure your big bold text tells them exactly what they get.',
    gradientClass: 'from-blue-400 to-indigo-500',
  },
  {
    id: 't2',
    title: 'Newsletter Sign-up',
    description:
      'A clean, simple page just for getting people to join your newsletter.',
    typeId: 'lead',
    category: 'Software & Apps',
    difficulty: 'Beginner',
    bestForTag: 'Perfect for writers',
    stepCount: 2,
    strategyNote:
      'Show them a sneak peek of your best email to get them excited.',
    gradientClass: 'from-emerald-400 to-teal-500',
  },
  {
    id: 't3',
    title: 'Sell a Digital Guide',
    description: 'Everything you need to sell an ebook or short course online.',
    typeId: 'product',
    category: 'Teaching & Courses',
    difficulty: 'Intermediate',
    bestForTag: 'Great for teachers',
    stepCount: 4,
    strategyNote:
      'Offer them a helpful template right before they pay for a little extra cash.',
    gradientClass: 'from-purple-400 to-pink-500',
  },
  {
    id: 't4',
    title: 'Online Store Checkout',
    description:
      'A smooth way to sell physical items and offer them more stuff after they buy.',
    typeId: 'product',
    category: 'Online Store',
    difficulty: 'Advanced',
    bestForTag: 'Perfect for online stores',
    stepCount: 5,
    strategyNote:
      'Offer them a discount if they buy a second one right after checkout.',
    gradientClass: 'from-orange-400 to-red-500',
  },
  {
    id: 't5',
    title: 'Webinar Sign-up',
    description: 'Get people to sign up for your online video presentation.',
    typeId: 'presentation',
    category: 'Services',
    difficulty: 'Intermediate',
    bestForTag: 'Great for expensive items',
    stepCount: 4,
    strategyNote:
      "Make them curious! Don't give away the secret on the sign-up page.",
    gradientClass: 'from-cyan-400 to-blue-500',
  },
  {
    id: 't6',
    title: 'Video Sales Page',
    description:
      'A simple page with a video that does all the selling for you.',
    typeId: 'presentation',
    category: 'Health & Fitness',
    difficulty: 'Advanced',
    bestForTag: 'Great for health products',
    stepCount: 3,
    strategyNote:
      'The first 5 seconds of the video need to grab their attention immediately.',
    gradientClass: 'from-yellow-400 to-orange-500',
  },
  {
    id: 't7',
    title: 'Book a Call',
    description:
      'Make sure people are a good fit before they take up your time.',
    typeId: 'appointment',
    category: 'Local Services',
    difficulty: 'Intermediate',
    bestForTag: 'Perfect for agencies',
    stepCount: 3,
    strategyNote:
      'Ask a few questions to make sure they can afford you before they book.',
    gradientClass: 'from-slate-600 to-slate-800',
  },
  {
    id: 't8',
    title: '5-Day Challenge',
    description:
      'Get people to join a fun multi-day event with an option to pay for VIP access.',
    typeId: 'challenge',
    category: 'Health & Fitness',
    difficulty: 'Advanced',
    bestForTag: 'Great for building a group',
    stepCount: 5,
    strategyNote:
      'The VIP upgrade should give them a chance to ask you questions directly.',
    gradientClass: 'from-rose-400 to-pink-600',
  },
  {
    id: 't9',
    title: 'Free Book (Just Pay Shipping)',
    description:
      'Give away a book for free, and just ask them to pay for the stamp.',
    typeId: 'product',
    category: 'Online Store',
    difficulty: 'Intermediate',
    bestForTag: 'Perfect for authors',
    stepCount: 4,
    strategyNote:
      'You make your real money when you offer them something else on the next page.',
    gradientClass: 'from-amber-300 to-orange-500',
  },
]

export const INLINE_TIPS = [
  '💡 Keep it simple! Your first page should do just one thing — get people interested.',
  "💡 Don't overthink it. A simple 2-page setup is usually all you need to get started.",
  '💡 Always click through your own pages to make sure everything works before showing it to people.',
  "💡 The big bold text at the top of your page is super important. Make sure it's catchy!",
]
